import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Avatar, Empty, Input, List, Skeleton, Tag, Typography,
} from 'antd';
import {
  CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { JobSituationDataProps, JobStatus } from './types';

type AreaReviewer = {
  id: number;
  displayName: string;
  statusStr: JobStatus;
};

type ReviewArea = {
  key: string;
  name: string;
  statusStr: JobStatus;
  users: AreaReviewer[];
};

const statusPriority: Record<JobStatus, number> = {
  accepted: 0,
  rejected: 1,
  pending: 2,
};

const normalizeSearch = (value: string) => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLocaleLowerCase();

const JobStatusToast = ({
  situations,
  loading,
}: {
  situations: JobSituationDataProps;
  loading: boolean;
}) => {
  const { Text } = Typography;
  const { t } = useTranslation();
  const [selectedAreaKey, setSelectedAreaKey] = useState('');
  const [query, setQuery] = useState('');

  const statusConfig = {
    accepted: { color: 'green', icon: <CheckCircleOutlined /> },
    pending: { color: 'gold', icon: <ClockCircleOutlined /> },
    rejected: { color: 'red', icon: <CloseCircleOutlined /> },
  };

  const reviewAreas = useMemo(() => {
    const areas = new Map<string, ReviewArea>();

    situations.forEach((situation) => {
      situation.users.forEach((user) => {
        const areaKey = user.area?.id
          ? `area-${user.area.id}`
          : `area-${normalizeSearch(user.area?.name || t('job.reviewerModal.noArea'))}`;
        const area = areas.get(areaKey);

        if (!area) {
          areas.set(areaKey, {
            key: areaKey,
            name: user.area?.contractorName || user.area?.name || t('job.reviewerModal.noArea'),
            statusStr: situation.statusStr,
            users: [{
              id: user.id,
              displayName: user.displayName,
              statusStr: situation.statusStr,
            }],
          });
          return;
        }

        if (statusPriority[situation.statusStr] > statusPriority[area.statusStr]) {
          area.statusStr = situation.statusStr;
        }

        const existingUser = area.users.find(({ id }) => id === user.id);
        if (!existingUser) {
          area.users.push({
            id: user.id,
            displayName: user.displayName,
            statusStr: situation.statusStr,
          });
        } else if (statusPriority[situation.statusStr]
          > statusPriority[existingUser.statusStr]) {
          existingUser.statusStr = situation.statusStr;
        }
      });
    });

    return Array.from(areas.values());
  }, [situations, t]);

  useEffect(() => {
    const initialArea = reviewAreas.find(({ statusStr }) => statusStr === 'pending')
      || reviewAreas[0];
    setSelectedAreaKey(initialArea?.key || '');
    setQuery('');
  }, [reviewAreas]);

  const normalizedQuery = normalizeSearch(query);
  const visibleAreas = useMemo(() => reviewAreas
    .filter(({ users }) => !normalizedQuery
      || users.some(({ displayName }) => normalizeSearch(displayName)
        .includes(normalizedQuery))), [
    normalizedQuery, reviewAreas,
  ]);

  const selectedArea = reviewAreas.find(({ key }) => key === selectedAreaKey);
  const selectedReviewers = useMemo(() => (selectedArea?.users || [])
    .filter(({ displayName }) => !normalizedQuery
      || normalizeSearch(displayName).includes(normalizedQuery)), [
    normalizedQuery, selectedArea,
  ]);

  const handleSearch = useCallback((value: string) => {
    setQuery(value);
    const search = normalizeSearch(value);
    if (!search) return;

    const matchingArea = reviewAreas.find(({ users }) => users
      .some(({ displayName }) => normalizeSearch(displayName).includes(search)));
    if (matchingArea) setSelectedAreaKey(matchingArea.key);
  }, [reviewAreas]);

  const initials = (displayName: string) => displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((name) => name[0])
    .join('')
    .toUpperCase();

  return (
    <Skeleton loading={loading} active paragraph={{ rows: 6 }}>
      {reviewAreas.length === 0 ? (
        <Empty description={t('job.reviewerModal.noReviewers')} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <section className="md:col-span-2 border border-gray-200 rounded-lg p-3">
            <Text className="block font-bold text-xs mb-3">
              {t('job.reviewerModal.reviewAreas').toUpperCase()}
            </Text>
            <Input
              allowClear
              prefix={<SearchOutlined className="text-gray-400" />}
              placeholder={t('job.reviewerModal.search')}
              value={query}
              onChange={({ target }) => handleSearch(target.value)}
              className="mb-3"
            />
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {visibleAreas.map((area) => {
                const config = statusConfig[area.statusStr];
                const isSelected = selectedAreaKey === area.key;
                return (
                  <button
                    type="button"
                    key={area.key}
                    onClick={() => setSelectedAreaKey(area.key)}
                    className={`w-full min-h-14 flex items-center gap-2 p-3 border rounded-lg text-left transition-colors ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                  >
                    <span className="font-semibold flex-1 truncate">
                      {area.name}
                    </span>
                    <Tag color={config.color} icon={config.icon} className="m-0">
                      {t(`job.jobStatus.${area.statusStr}`)}
                    </Tag>
                    <Text type="secondary" className="whitespace-nowrap text-xs">
                      {t('job.reviewerModal.reviewerCount', { count: area.users.length })}
                    </Text>
                  </button>
                );
              })}
              {visibleAreas.length === 0 && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('job.reviewerModal.noReviewers')}
                />
              )}
            </div>
          </section>

          <section className="md:col-span-3 border border-gray-200 rounded-lg p-3">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Text className="font-bold text-xs">
                {t('job.reviewerModal.reviewersOf', {
                  area: selectedArea?.name || '',
                }).toUpperCase()}
              </Text>
              {selectedArea && (
                <Tag
                  color={statusConfig[selectedArea.statusStr].color}
                  icon={statusConfig[selectedArea.statusStr].icon}
                >
                  {t(`job.jobStatus.${selectedArea.statusStr}`)}
                </Tag>
              )}
            </div>
            <Text type="secondary" className="block mb-3">
              {t('job.reviewerModal.helper')}
            </Text>
            <List
              className="max-h-72 overflow-y-auto pr-1"
              locale={{ emptyText: t('job.reviewerModal.noReviewers') }}
              dataSource={selectedReviewers}
              renderItem={(reviewer) => (
                <List.Item className="border border-gray-200 rounded-lg px-3 mb-2">
                  <List.Item.Meta
                    avatar={<Avatar className="bg-yellow-100 text-yellow-700">{initials(reviewer.displayName)}</Avatar>}
                    title={<Text className="font-semibold">{reviewer.displayName}</Text>}
                    description={selectedArea?.name}
                  />
                  <Tag color={statusConfig[reviewer.statusStr].color}>
                    {t(`job.jobStatus.${reviewer.statusStr}`)}
                  </Tag>
                </List.Item>
              )}
            />
          </section>
        </div>
      )}
    </Skeleton>
  );
};

export default JobStatusToast;
